import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { PromisePool } from 'es6-promise-pool';
import { useIntl } from 'react-intl';
import {
  Button,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { showModalTop } from '../../actions';

import {
  APIcheckExistingFiles,
  APIcreateFolderCollections,
} from '../../api/Collections';
import { APIupdateFile } from '../../api/Files';

import BulkVersioningFileList from './FileList';

import {
  accountUsageLock,
  subscriptionExpired,
} from '../../helpers/App';
import { userCan } from '../../helpers/Auth';
import { removeUnwantedFiles } from '../../helpers/Files';
import withUploader from '../../helpers/withUploader';

import ErrorBoundary from '../ErrorBoundary';
import Loading from '../Loading';

const BulkVersioningModal = ({
  uploader,
  submittedFiles: uploaderSubmittedFiles,
  collection,
  profile,
  openModal,
  handleClose,
}) => {
  const intl = useIntl();
  const folderInput = useRef();

  const [submittedFiles, setSubmittedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState({});
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    const fileFolders = uploaderSubmittedFiles.filter(sf => (sf.folder)).map(sf => (sf.folder.replace('\\', '/')));
    setFolders([...new Set(fileFolders)]);
  }, [uploaderSubmittedFiles]);

  useEffect(() => {
    const loadDiff = async () => {
      setLoading(true);
      setExistingFiles({});
      setError(null);
      try {
        const filePaths = submittedFiles.map(f => (f.hdcFullPath));
        const response = await APIcheckExistingFiles(collection.uuid, filePaths);
        if (response.status !== 200) {
          throw new Error(intl.formatMessage({ defaultMessage: 'Something went wrong' }));
        }
        setExistingFiles(response.data);
      } catch (e) {
        setError(e.response.data.message);
      } finally {
        setLoading(false);
      }
    };

    if (submittedFiles.length > 0) {
      loadDiff();
    }
  }, [submittedFiles, intl, collection]);

  const fileExistsUuid = (path) => {
    const key = Object.keys(existingFiles).find(ef => (ef === path));
    return key ? existingFiles[key] : null;
  };

  const fileExists = useCallback((path) => (
    !!Object.keys(existingFiles).find(ef => (ef === path))
  ), [existingFiles]);

  const initUploadFolder = (e) => {
    const { files } = e.target;

    if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
      return;
    }

    const nFiles = Array.from(files);
    nFiles.forEach((file, idx) => {
      if (file.webkitRelativePath) {
        nFiles[idx].hdcFullPath = `/${file.webkitRelativePath}`;
      }
      nFiles[idx].hdcFullPath = nFiles[idx].hdcFullPath.split('/').filter(el => el).slice(1).join('/');
    });

    if (files.length > 0) {
      setSubmittedFiles([...removeUnwantedFiles(nFiles)]);
      folderInput.current.value = null;
    } else {
      setSubmittedFiles([]);
    }
  };

  const handleUpload = () => {
    setUploading(true);

    const nFiles = Array.from(submittedFiles);
    nFiles.forEach((file, idx) => {
      if (fileExists(file.hdcFullPath)) {
        nFiles[idx].parent_uuid = fileExistsUuid(file.hdcFullPath);
        nFiles[idx].id = idx;
      }
    });

    uploader.methods.addFiles(nFiles);
  };

  const handleSaveFiles = useCallback((folderCollections = []) => {
    const getFolderCollection = (file) => {
      const fileFolder = `/${file.folder}`;
      return (file.folder && folderCollections[fileFolder]) ?
        [folderCollections[fileFolder]] :
        [{ uuid: collection.uuid }];
    };

    let counter = 0;
    const newFiles = uploaderSubmittedFiles.filter(f => (!fileExists(`${f.folder}/${f.original_name}`)));
    const promiseProducer = () => {
      if (counter < newFiles.length) {
        const file = newFiles[counter];
        counter += 1;
        return APIupdateFile({
          ...file,
          searchable: true,
          uploadStatus: 'completed',
          mapping: true,
          collections: getFolderCollection(file),
          bulk_versioning: true,
        });
      }
      return null;
    };

    const pool = new PromisePool(promiseProducer, 2);
    pool.start().then(() => {
      setUploading(false);
      setFinished(true);
    });
  }, [uploaderSubmittedFiles, collection, fileExists]);

  const totalFiles = uploaderSubmittedFiles.length;
  const uploadedFiles = uploaderSubmittedFiles.filter(f => (f.uploadStatus === 'completed')).length;
  const completed = uploadedFiles > 0 && totalFiles === uploadedFiles;

  useEffect(() => {
    if (completed && !saving) {
      setSaving(true);
      if (folders.length === 0) {
        handleSaveFiles();
      } else {
        APIcreateFolderCollections({
          folders,
          parent_collection_uuid: collection.uuid,
          update_existing: true,
        }).then((res) => {
          handleSaveFiles(res.data);
        });
      }
    }
  }, [completed, finished, saving, folders, collection, handleSaveFiles]);

  if (!userCan('file.upload') || !collection) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Input
        type="file"
        multiple={true}
        name="folder"
        className="upload-file hidden"
        onChange={initUploadFolder}
        innerRef={folderInput}
        webkitdirectory="true"
        id="bulk-versioning-folder-input"
      />

      <ModalHeader>
        {intl.formatMessage({ defaultMessage: 'Bulk versioning' })}
      </ModalHeader>
      <ModalBody>
        {error ? (
          <p className="error center">{error}</p>
        ) : (
          <>
            {finished ? (
              <div className="center">
                <p>
                  {intl.formatMessage({ defaultMessage: 'All files have been updated and/or uploaded.' })}
                </p>
                <Button
                  tag={Link}
                  onClick={handleClose}
                  to={`/collections/${collection.uuid}`}
                >
                  {intl.formatMessage({ defaultMessage: 'Go to Collection' })}
                </Button>
              </div>
            ) : (
              <>
                <div className="center bulk-versioning-message mb-3">
                  <div className="mb-2">
                    {intl.formatMessage({ defaultMessage: 'Select a folder to update all versions of the files inside the <link></link> Collection. New files will be added separately.' }, {
                      link: () => (
                        <Link target="_blank" to={`/collections/${collection.uuid}`}>
                          {collection.name}
                        </Link>
                      ),
                    })}
                  </div>
                  <Button className="mr-2" size="sm" tag="a" href="https://help.stockpress.co/en/articles/11165492-bulk-versioning-files" target="_blank">
                    <i className="far fa-book mr-1" />
                    {intl.formatMessage({ defaultMessage: 'Learn more' })}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      openModal(
                        'https://player.vimeo.com/video/1079607966?h=6f20813473&badge=0&byline=0&title=0',
                        intl.formatMessage({ defaultMessage: 'Bulk versioning' }),
                      );
                    }}
                  >
                    <i className="far fa-play mr-1" />
                    {intl.formatMessage({ defaultMessage: 'View video' })}
                  </Button>
                </div>
                {loading ? (
                  <Loading />
                ) : (
                  <BulkVersioningFileList
                    uploader={uploader}
                    files={submittedFiles}
                    fileExists={fileExists}
                  />
                )}
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="link"
            onClick={handleClose}
            disabled={uploading}
          >
            {finished ? (
              intl.formatMessage({ defaultMessage: 'Close' })
            ) : (
              intl.formatMessage({ defaultMessage: 'Cancel' })
            )}
          </Button>
          {!finished && (
            <Button
              color="primary"
              onClick={handleUpload}
              disabled={!!error || loading || uploading}
            >
              {uploading ? (
                <>
                  {saving ? (
                    intl.formatMessage({ defaultMessage: 'Finishing...' })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Uploading ({uploadedFiles} of {totalFiles})...' }, {
                      uploadedFiles,
                      totalFiles,
                    })
                  )}
                </>
              ) : (
                intl.formatMessage({ defaultMessage: 'Upload' })
              )}
            </Button>
          )}
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

const mapDispatchToProps = dispatch => ({
  openModal: (url, title) => {
    dispatch(showModalTop('VideoPlayerModal', {
      className: 'modal-video-player',
      url,
      title,
      backdrop: true,
    }));
  },
});

BulkVersioningModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  uploader: PropTypes.object.isRequired,
  submittedFiles: PropTypes.array.isRequired,
  profile: PropTypes.object.isRequired,
  collection: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withUploader(BulkVersioningModal));
